import { useQuery } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
	countTikTokAdsLeadFormCustomQuestions,
	MAX_TIKTOK_ADS_LEAD_FORM_CUSTOM_QUESTIONS,
	type TikTokAdsInstantForm,
	type TikTokAdsLeadFormDraft,
	type TikTokAdsLeadFormQuestion,
	TikTokAdsLeadFormQuestionType,
} from "shared";
import { Api } from "../api.config";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { useTikTokCampaignWizard } from "./TikTokCampaignWizardContext";

type LeadFormMode = "create" | "existing";

const PREDEFINED_FIELDS: TikTokAdsLeadFormQuestion[] = [
	{
		type: TikTokAdsLeadFormQuestionType.FULL_NAME,
		key: "full_name",
		label: "Full Name",
	},
	{
		type: TikTokAdsLeadFormQuestionType.EMAIL,
		key: "email",
		label: "Email",
	},
	{
		type: TikTokAdsLeadFormQuestionType.PHONE_NUMBER,
		key: "phone_number",
		label: "Phone Number",
	},
];

function createEmptyLeadForm(): TikTokAdsLeadFormDraft {
	return {
		id: crypto.randomUUID(),
		name: "",
		headline: "",
		description: "",
		questions: [
			{
				type: TikTokAdsLeadFormQuestionType.EMAIL,
				key: "email",
				label: "Email",
			},
		],
		privacyPolicyUrl: "",
		thankYouTitle: "Thank you!",
		thankYouDescription: "We will get back to you shortly.",
	};
}

const FormPreview: FC<{ form: TikTokAdsLeadFormDraft }> = ({ form }) => (
	<div
		style={{
			border: "1px solid var(--bs-border-color, #dee2e6)",
			borderRadius: 12,
			padding: 20,
			maxWidth: 360,
			margin: "0 auto",
			background: "#fff",
		}}
	>
		<div
			style={{
				fontWeight: 600,
				fontSize: 16,
				marginBottom: 8,
				textAlign: "center",
			}}
		>
			{form.headline || form.name || "Untitled Form"}
		</div>
		{form.description && (
			<p
				style={{
					fontSize: 13,
					color: "#65676b",
					textAlign: "center",
					marginBottom: 12,
				}}
			>
				{form.description}
			</p>
		)}
		{form.questions.map((q) => (
			<div key={q.key} style={{ marginBottom: 10 }}>
				<label
					htmlFor={`tiktok-lead-preview-${q.key}`}
					style={{ fontSize: 12, display: "block", marginBottom: 4 }}
				>
					{q.label || "Question"}
				</label>
				<input
					id={`tiktok-lead-preview-${q.key}`}
					className="form-control form-control-sm"
					disabled
					placeholder={q.label || "…"}
				/>
			</div>
		))}
		<button type="button" className="btn btn-primary btn-sm w-100" disabled>
			Submit
		</button>
		{form.privacyPolicyUrl && (
			<p
				className="text-muted"
				style={{ fontSize: 11, marginTop: 10, marginBottom: 0 }}
			>
				By submitting, you agree to the privacy policy.
			</p>
		)}
	</div>
);

const InstantFormPicker: FC<{
	selectedId: string;
	onSelect: (form: TikTokAdsInstantForm) => void;
	id: string;
}> = ({ selectedId, onSelect, id }) => {
	const { data, isLoading, isError, refetch, isFetching } = useQuery({
		queryKey: ["TIKTOK_ADS", "INSTANT_FORMS"],
		queryFn: () => Api.GetTikTokAdsInstantForms().then((r) => r.data),
	});

	if (isLoading) {
		return (
			<div className="d-flex align-items-center gap-2">
				<Select id={id} disabled aria-busy="true">
					<option>Loading Instant Forms…</option>
				</Select>
				<Spinner size="sm" />
			</div>
		);
	}

	if (isError) {
		return (
			<>
				<p className="text-danger mb-2">
					Could not load Instant Forms from TikTok. Paste a Page ID below to
					continue.
				</p>
				<Form.Control
					id={id}
					value={selectedId}
					onChange={(e) => {
						const pageId = e.target.value.trim();
						onSelect({
							pageId,
							title: pageId || "Manual Page ID",
						});
					}}
					placeholder="e.g. 7681235238407913749"
				/>
			</>
		);
	}

	const forms = data ?? [];

	return (
		<>
			<div
				style={{
					display: "flex",
					gap: 8,
					alignItems: "center",
					marginBottom: 8,
				}}
			>
				{forms.length > 0 ? (
					<Select
						id={id}
						value={selectedId}
						onChange={(e) => {
							const form = forms.find((f) => f.pageId === e.target.value);
							if (form) onSelect(form);
						}}
					>
						<option value="">Select Instant Form…</option>
						{forms.map((form) => (
							<option key={form.pageId} value={form.pageId}>
								{form.title}
								{form.status ? ` (${form.status})` : ""}
							</option>
						))}
					</Select>
				) : (
					<Form.Control
						id={id}
						value={selectedId}
						onChange={(e) => {
							const pageId = e.target.value.trim();
							onSelect({
								pageId,
								title: pageId || "Manual Page ID",
							});
						}}
						placeholder="Paste Page ID (e.g. 7681235238407913749)"
					/>
				)}
				<Button
					size="sm"
					variant="secondary"
					disabled={isFetching}
					onClick={() => refetch()}
				>
					{isFetching ? "Refreshing…" : "Refresh"}
				</Button>
			</div>
			{forms.length === 0 && (
				<p className="text-muted mb-0">
					API list is empty while forms are Pending review. Paste the Page ID
					from Ads Manager, or wait until the form is attached to an ad and
					published.
				</p>
			)}
		</>
	);
};

export const StepTikTokLeadForm: FC = () => {
	const { draft, updateDraft, nextStep, prevStep } = useTikTokCampaignWizard();
	const [error, setError] = useState("");
	const [mode, setMode] = useState<LeadFormMode>(
		draft.leadForm && !draft.instantFormPageId
			? "create"
			: draft.instantFormPageId && !draft.leadForm
				? "existing"
				: draft.leadForm
					? "create"
					: "existing",
	);

	const form: TikTokAdsLeadFormDraft = draft.leadForm ?? createEmptyLeadForm();

	const switchMode = (next: LeadFormMode) => {
		setMode(next);
		setError("");
		if (next === "create") {
			updateDraft({
				leadForm: draft.leadForm ?? createEmptyLeadForm(),
				instantFormPageId: draft.instantFormPageId ?? null,
			});
		} else {
			updateDraft({
				leadForm: null,
			});
		}
	};

	const updateForm = (partial: Partial<TikTokAdsLeadFormDraft>) => {
		updateDraft({ leadForm: { ...form, ...partial } });
	};

	const isPredefinedActive = (key: string) =>
		form.questions.some((q) => q.key === key);

	const togglePredefined = (field: TikTokAdsLeadFormQuestion) => {
		if (isPredefinedActive(field.key)) {
			updateForm({
				questions: form.questions.filter((q) => q.key !== field.key),
			});
		} else {
			updateForm({ questions: [...form.questions, field] });
		}
	};

	const addCustomQuestion = () => {
		if (
			countTikTokAdsLeadFormCustomQuestions(form.questions) >=
			MAX_TIKTOK_ADS_LEAD_FORM_CUSTOM_QUESTIONS
		) {
			return;
		}
		updateForm({
			questions: [
				...form.questions,
				{
					type: TikTokAdsLeadFormQuestionType.CUSTOM,
					key: `custom_${Date.now()}`,
					label: "",
				},
			],
		});
	};

	const updateCustomLabel = (key: string, label: string) => {
		updateForm({
			questions: form.questions.map((q) =>
				q.key === key ? { ...q, label } : q,
			),
		});
	};

	const removeQuestion = (key: string) => {
		updateForm({
			questions: form.questions.filter((q) => q.key !== key),
		});
	};

	const handleNext = () => {
		if (!draft.instantFormPageId?.trim()) {
			setError(
				mode === "create"
					? "Create the Instant Form in TikTok Ads Manager, refresh, then select it below."
					: "Select an Instant Form from your TikTok advertiser.",
			);
			return;
		}
		if (mode === "create") {
			if (!form.name.trim()) {
				setError("Form name is required.");
				return;
			}
			if (!form.privacyPolicyUrl.trim()) {
				setError("Privacy policy URL is required.");
				return;
			}
			if (form.questions.length === 0) {
				setError("Add at least one question.");
				return;
			}
			if (
				form.questions.some(
					(q) =>
						q.type === TikTokAdsLeadFormQuestionType.CUSTOM && !q.label.trim(),
				)
			) {
				setError("Custom questions need a label.");
				return;
			}
			updateDraft({ leadForm: form });
		}
		setError("");
		nextStep();
	};

	return (
		<Card>
			<Card.Header>Lead Form</Card.Header>
			<Card.Body>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					<div>
						<p className="form-label" style={{ marginBottom: 8 }}>
							How would you like to set up the Instant Form?
						</p>
						<div className="d-flex gap-3 flex-wrap">
							<div className="form-check">
								<input
									className="form-check-input"
									type="radio"
									id="tt-lead-form-mode-create"
									name="tt-lead-form-mode"
									checked={mode === "create"}
									onChange={() => switchMode("create")}
								/>
								<label
									className="form-check-label"
									htmlFor="tt-lead-form-mode-create"
								>
									Create new form
								</label>
							</div>
							<div className="form-check">
								<input
									className="form-check-input"
									type="radio"
									id="tt-lead-form-mode-existing"
									name="tt-lead-form-mode"
									checked={mode === "existing"}
									onChange={() => switchMode("existing")}
								/>
								<label
									className="form-check-label"
									htmlFor="tt-lead-form-mode-existing"
								>
									Use existing Instant Form from TikTok
								</label>
							</div>
						</div>
					</div>

					{mode === "existing" ? (
						<div>
							<label className="form-label" htmlFor="tiktok-instant-form">
								Instant Form
							</label>
							<InstantFormPicker
								id="tiktok-instant-form"
								selectedId={draft.instantFormPageId ?? ""}
								onSelect={(instantForm) =>
									updateDraft({
										instantFormPageId: instantForm.pageId,
										leadForm: null,
									})
								}
							/>
						</div>
					) : (
						<>
							<div className="alert alert-info mb-0" role="alert">
								TikTok Instant Forms are created in Ads Manager (or via TikTok’s
								Instant Page Editor SDK). Plan the form here, create it in
								TikTok, then select the published form below so Moxie can attach
								its <code>page_id</code> to the ad.
							</div>

							<div
								style={{
									display: "grid",
									gridTemplateColumns: "minmax(0, 1fr) 320px",
									gap: 24,
								}}
							>
								<div
									style={{ display: "flex", flexDirection: "column", gap: 12 }}
								>
									<h6 className="mb-0">Form builder</h6>
									<div>
										<label className="form-label" htmlFor="tt-form-name">
											Form name
										</label>
										<input
											id="tt-form-name"
											type="text"
											className="form-control"
											value={form.name}
											onChange={(e) => updateForm({ name: e.target.value })}
											placeholder="e.g. Contact Us Form"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="tt-form-headline">
											Headline
										</label>
										<input
											id="tt-form-headline"
											type="text"
											className="form-control"
											value={form.headline}
											onChange={(e) => updateForm({ headline: e.target.value })}
											placeholder="Get a free quote"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="tt-form-description">
											Description
										</label>
										<textarea
											id="tt-form-description"
											className="form-control"
											rows={2}
											value={form.description}
											onChange={(e) =>
												updateForm({ description: e.target.value })
											}
											placeholder="Brief description shown above the fields"
										/>
									</div>

									<h6>Questions</h6>
									{PREDEFINED_FIELDS.map((field) => (
										<div className="form-check" key={field.key}>
											<input
												className="form-check-input"
												type="checkbox"
												id={`tt-field-${field.key}`}
												checked={isPredefinedActive(field.key)}
												onChange={() => togglePredefined(field)}
											/>
											<label
												className="form-check-label"
												htmlFor={`tt-field-${field.key}`}
											>
												{field.label}
											</label>
										</div>
									))}

									{form.questions
										.filter(
											(q) => q.type === TikTokAdsLeadFormQuestionType.CUSTOM,
										)
										.map((q) => (
											<div
												key={q.key}
												style={{
													display: "flex",
													gap: 8,
													alignItems: "center",
												}}
											>
												<input
													type="text"
													className="form-control form-control-sm"
													value={q.label}
													onChange={(e) =>
														updateCustomLabel(q.key, e.target.value)
													}
													placeholder="Custom question label"
													style={{ flex: 1 }}
												/>
												<button
													type="button"
													className="btn btn-sm btn-outline-danger"
													onClick={() => removeQuestion(q.key)}
												>
													Remove
												</button>
											</div>
										))}

									<button
										type="button"
										className="btn btn-outline-primary btn-sm align-self-start"
										onClick={addCustomQuestion}
										disabled={
											countTikTokAdsLeadFormCustomQuestions(form.questions) >=
											MAX_TIKTOK_ADS_LEAD_FORM_CUSTOM_QUESTIONS
										}
									>
										+ Add custom question
									</button>

									<div>
										<label className="form-label" htmlFor="tt-privacy-url">
											Privacy policy URL
										</label>
										<input
											id="tt-privacy-url"
											type="url"
											className="form-control"
											value={form.privacyPolicyUrl}
											onChange={(e) =>
												updateForm({ privacyPolicyUrl: e.target.value })
											}
											placeholder="https://example.com/privacy"
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="tt-thanks-title">
											Thank-you title
										</label>
										<input
											id="tt-thanks-title"
											type="text"
											className="form-control"
											value={form.thankYouTitle}
											onChange={(e) =>
												updateForm({ thankYouTitle: e.target.value })
											}
										/>
									</div>
									<div>
										<label className="form-label" htmlFor="tt-thanks-body">
											Thank-you message
										</label>
										<textarea
											id="tt-thanks-body"
											className="form-control"
											rows={2}
											value={form.thankYouDescription}
											onChange={(e) =>
												updateForm({ thankYouDescription: e.target.value })
											}
										/>
									</div>
								</div>

								<div>
									<p className="form-label">Preview</p>
									<FormPreview form={form} />
								</div>
							</div>

							<div
								style={{
									borderTop: "1px solid var(--bs-border-color, #dee2e6)",
									paddingTop: 16,
								}}
							>
								<h6>Publish Instant Form in TikTok</h6>
								<p className="text-secondary" style={{ fontSize: "0.9rem" }}>
									1. Open TikTok Ads Manager → Assets → Instant Forms
									<br />
									2. Create a form matching the fields above (and set it to
									Published)
									<br />
									3. Refresh below and select that form
								</p>
								<div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
									<a
										className="btn btn-sm btn-outline-primary"
										href="https://ads.tiktok.com/"
										target="_blank"
										rel="noreferrer"
									>
										Open TikTok Ads Manager
									</a>
								</div>
								<label
									className="form-label"
									htmlFor="tiktok-published-instant-form"
								>
									Published Instant Form
								</label>
								<InstantFormPicker
									id="tiktok-published-instant-form"
									selectedId={draft.instantFormPageId ?? ""}
									onSelect={(instantForm) =>
										updateDraft({
											instantFormPageId: instantForm.pageId,
											leadForm: form,
										})
									}
								/>
							</div>
						</>
					)}

					{error && <p className="text-danger mb-0">{error}</p>}

					<div style={{ display: "flex", gap: 8 }}>
						<Button variant="secondary" onClick={prevStep}>
							Back
						</Button>
						<Button onClick={handleNext}>Continue</Button>
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
